import { Directive, HostListener, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { HelperService } from '../services/helper.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { OverlayService } from '../services/overlayService';
import { UrlShortenerService } from '../services/url-shortener.service';
import { ShortenedUrlInfo } from '../interfaces';
import { GeneralUtilsService } from '../services/general-utils.service';

@Directive({
  selector: '[shortenedUrl]',
  standalone: false,
})
export class ShortenedUrlDirective implements OnChanges {
  @Input() sourceUrl: string;
  @Input() readOnlyLink: boolean = true;
  shortenedUrl: string = '';
  shortenedInfo: ShortenedUrlInfo | null = null;
  isPending: boolean = false;
  private readonly generalUtilsService = inject(GeneralUtilsService);
  private readonly helperService = inject(HelperService);

  constructor(
    private readonly urlShortenerService: UrlShortenerService,
    private readonly overlayService: OverlayService,
    private readonly translatePipe: TranslatePipe
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.linkSource || changes.sourceUrl) {
      this.shortenedInfo = null;
      this.shortenedUrl = '';
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.sourceUrl || this.isPending) {
      return;
    }

    if (this.shortenedUrl) {
      this.copyShortenedUrl();
      return;
    }
    this.getShortenedUrl();
  }

  getShortenedUrl() {
    this.isPending = true;
    this.urlShortenerService.getShortenedUrl(this.sourceUrl).subscribe({
      next: (result: ShortenedUrlInfo) => {
        this.shortenedUrl = result.shortenedUrl;
        this.shortenedInfo = result;
        this.copyShortenedUrl();
        this.isPending = false;
      },
      error: (error: any) => {
        if (error.status == 404 || error.errorCode == 'NOT_FOUND') {
          this.createShortenedUrl();
        } else {
          this.copyOriginalUrl();
          console.error('Failed to create shortened url', error);
          this.isPending = false;
        }
      },
    });
  }

  private copyShortenedUrl() {
    this.generalUtilsService.copyToClipboard(this.shortenedUrl);
    this.overlayService.showSuccess(this.translatePipe.transform('copiedToClipboard'));
  }

  private copyOriginalUrl() {
    this.generalUtilsService.copyToClipboard(this.sourceUrl);
    this.overlayService.showSuccess(this.translatePipe.transform('copiedToClipboard'));
  }

  private createShortenedUrl() {
    const info: ShortenedUrlInfo = {
      backHalf: '',
      shortenedUrl: '',
      domain: this.helperService.getCurrentOrigin(),
      redirectionUrl: this.sourceUrl,
      tags: [],
      title: '',
    };
    this.urlShortenerService.createShortenedUrl(info).subscribe({
      next: (result: ShortenedUrlInfo) => {
        this.shortenedInfo = result;
        this.shortenedUrl = result.shortenedUrl;
        this.copyShortenedUrl();
        this.isPending = false;
      },
      error: (error: unknown) => {
        this.copyOriginalUrl();
        console.error('Failed to create shortened url', error);
        this.isPending = false;
      },
    });
  }
}
